import React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

interface DataPreviewTableProps {
  headers: string[];
  sampleData: Record<string, string>[];
}

/**
 * DataPreviewTable renders a table showing file headers and up to 5 rows of sample data.
 * The table is constrained to its parent width and scrolls horizontally.
 */
const DataPreviewTable: React.FC<DataPreviewTableProps> = ({
  headers,
  sampleData,
}) => {
  const displayData = sampleData.slice(0, 5);

  if (headers.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Sample Data Preview
      </Typography>
      <Box
        sx={{
          maxWidth: 'calc(100vw - 350px)',
          overflow: 'hidden',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TableContainer
          sx={{
            maxHeight: 300,
            overflowX: 'auto',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              height: 8,
              width: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#bdbdbd',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <Table size="small" stickyHeader aria-label="Sample data preview">
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      backgroundColor: 'grey.100',
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {headers.map((header) => (
                    <TableCell
                      key={`${rowIndex}-${header}`}
                      sx={{
                        whiteSpace: 'nowrap',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={row[header] ?? ''}
                    >
                      {row[header] ?? ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {sampleData.length > 5 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Showing first 5 of {sampleData.length} sample rows
        </Typography>
      )}
    </Box>
  );
};

export default DataPreviewTable;
